<script>
    import {Tween} from 'svelte/motion';
    import {cubicOut} from 'svelte/easing';

    import {ElevationRideTypes, PaceRideTypes} from '@js/components/modules/constants';
    import {RIDE_TYPE_ICON} from '@js/components/themes/icons';
    import I18n from '@js/modules/translations';
    import Computation from '@js/modules/computation';
    import StorageService from '@js/modules/storage';
    import {computeRideDuration} from '@js/actions/mapActions';
    import AnalyticsService from '@js/modules/analyticsService';

    import {userStore} from '@js/stores/userStore';
    import {mapStore} from '@js/stores/mapStore';

    import Icon from '@js/components/themes/Icon.svelte';
    import Spinner from '@js/components/themes/Spinner.svelte';

    const RIDE_TYPE_STORAGE_KEY = 'MapEstimateRideType';

    const {
        estimateDuration = false
    } = $props();

    let _previousDistance = mapStore.currentDistance;
    // Restore previously selected ride type for dropdown pre-selection and auto-estimation
    const _storedRideType = estimateDuration ? StorageService.getItem(RIDE_TYPE_STORAGE_KEY) : null;
    let _hasAutoEstimated = false;

    let isDropdownOpen = $state(false);
    let isEstimating = $state(false);
    let hasEstimated = $state(false);

    const needsEstimation = $derived(estimateDuration && !$mapStore.currentDuration);

    if (_storedRideType) {
        mapStore.currentRideType = _storedRideType;
        hasEstimated = true;
    }
    const distanceProgress = new Tween(mapStore.currentDistance, {
        duration: 800,
        easing: cubicOut
    });

    const durationProgress = new Tween(mapStore.currentDuration, {
        duration: 800,
        easing: cubicOut
    });

    let outlineDataEl = $state(null);

    const clickOutside = (element, callbackFunction) => {
        function onClick(event) {
            if (!element || !outlineDataEl) {
                return;
            }

            if (!element.contains(event.target) && !outlineDataEl.contains(event.target)) {
                callbackFunction();
            }
        }

        document.body.addEventListener('click', onClick);

        return {
            update(newCallbackFunction) {
                callbackFunction = newCallbackFunction;
            },
            destroy() {
                document.body.removeEventListener('click', onClick);
            }
        };
    };

    const _closeDropdown = () => {
        isDropdownOpen = false;
    };

    const _handleEstimateClick = () => {
        if (!$mapStore.currentDistance) {
            return;
        }

        isDropdownOpen = !isDropdownOpen;
    };

    const _handleRideTypeSelect = (rideType) => {
        isDropdownOpen = false;

        if (!mapStore.map || isEstimating) {
            return;
        }

        isEstimating = true;
        hasEstimated = true;
        mapStore.currentRideType = rideType;

        AnalyticsService.trackMapComputeDuration(rideType);

        const serializedPoints = mapStore.map.serializePoints();

        computeRideDuration({
            mapPoints: serializedPoints,
            distance: mapStore.currentDistance,
            rideType
        })
            .then((result) => {
                if (!result) {
                    return;
                }

                mapStore.currentDuration = result.duration;
                mapStore.durationComputed = false;

                mapStore.currentSpeed = Computation.computeSpeed(mapStore.currentDistance, result.chronoDuration);
                mapStore.speedComputed = true;

                const positiveElevation = ElevationRideTypes.includes(rideType)
                    ? (mapStore.elevationApsis?.positiveElevation || 0)
                    : 0;
                const weight = window.currentUserWeight || 0;
                mapStore.currentCalories = Computation.estimateCalories(rideType, result.duration, mapStore.currentDistance, positiveElevation, weight);

                StorageService.setItem(RIDE_TYPE_STORAGE_KEY, rideType);
            })
            .finally(() => {
                isEstimating = false;
            });
    };

    $effect(() => {
        distanceProgress.target = $mapStore.currentDistance;
    });
    $effect(() => {
        durationProgress.target = $mapStore.currentDuration;

        if (estimateDuration && $mapStore.currentDuration && !hasEstimated) {
            hasEstimated = true;
        }
    });

    // Auto-estimate duration when initial points are present and a ride type was previously selected
    $effect(() => {
        if (_hasAutoEstimated || !_storedRideType || !$mapStore.currentDistance || !$mapStore.map || $mapStore.currentDuration) {
            return;
        }

        _hasAutoEstimated = true;
        _handleRideTypeSelect(_storedRideType);
    });

    // Re-estimate duration when distance changes after user has estimated once
    $effect(() => {
        const distance = $mapStore.currentDistance;

        if (distance !== _previousDistance) {
            _previousDistance = distance;

            if (hasEstimated && distance) {
                _handleRideTypeSelect($mapStore.currentRideType);
            }
        }
    });

</script>

<div class="map-data-outline">
    <div class="outline-container">
        <div class="outline-data">
            <div class="outline-value"
                 class:outline-value-on={$mapStore.currentDistance}>
                {Computation.formatDistance(distanceProgress.current, {
                    unit: $userStore.unit,
                    smartUnit: $userStore.unit !== 'mi',
                    kUnit: $userStore.unit === 'mi'
                })}
            </div>

            <div class="outline-name">
                {I18n.t('js.maps.outline.distance')}
            </div>
        </div>

        <div class="outline-data relative" bind:this={outlineDataEl}>
            {#if isEstimating}
                <div class="outline-value">
                    <Spinner size="4" color="primary"/>
                </div>
                <div class="outline-name">
                    {I18n.t('js.maps.outline.duration')}
                </div>
            {:else if needsEstimation}
                <div class="outline-duration-estimate relative flex flex-col items-center">
                    <button type="button"
                            class="outline-value inline-flex items-center gap-1 text-xs italic text-primary-600 hover:text-primary-800 cursor-pointer"
                            onclick={_handleEstimateClick}>
                        <svg class="w-4 h-4"
                             xmlns="http://www.w3.org/2000/svg"
                             fill="none"
                             viewBox="0 0 24 24"
                             stroke-width="2"
                             stroke="currentColor">
                            <path stroke-linecap="round"
                                  stroke-linejoin="round"
                                  d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/>
                        </svg>
                        {I18n.t('js.maps.outline.estimate')}
                    </button>

                    <div class="outline-name text-primary-600">
                        {I18n.t('js.maps.outline.duration')}
                    </div>

                    {#if isDropdownOpen}
                        <div class="absolute top-full left-1/2 -translate-x-1/2 mt-2 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50 min-w-[160px]"
                             use:clickOutside={_closeDropdown}>
                            <div class="px-3 py-1.5 text-xs text-gray-400 italic">
                                {I18n.t('js.ride.map.estimate_duration.menu')}
                            </div>
                            {#each Object.keys(RIDE_TYPE_ICON) as rideType (rideType)}
                                <button type="button"
                                        class="flex items-center gap-2 w-full px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
                                        onclick={() => _handleRideTypeSelect(rideType)}>
                                    <Icon class="h-4 w-4"
                                          icon={RIDE_TYPE_ICON[rideType]}/>
                                    {I18n.t(`js.ride.enums.ride_type.${rideType}`)}
                                </button>
                            {/each}
                        </div>
                    {/if}
                </div>
            {:else}
                <button type="button"
                        class="outline-duration-estimate relative flex flex-col items-center cursor-pointer"
                        onclick={estimateDuration ? _handleEstimateClick : undefined}>
                    <span class="outline-value inline-flex items-center gap-1">
                        {Computation.formatDuration(durationProgress.current, {withLeadingZero: false}) || '-'}

                        {#if estimateDuration && hasEstimated && RIDE_TYPE_ICON[$mapStore.currentRideType]}
                            <Icon class="h-4 w-4 text-gray-400"
                                  icon={RIDE_TYPE_ICON[$mapStore.currentRideType]}/>
                        {/if}
                    </span>

                    <span class="outline-name">
                        {I18n.t('js.maps.outline.duration')}
                    </span>
                </button>

                {#if isDropdownOpen}
                    <div class="absolute top-full left-1/2 -translate-x-1/2 mt-2 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50 min-w-[160px]"
                         use:clickOutside={_closeDropdown}>
                        <div class="px-3 py-1.5 text-xs text-gray-400 italic">
                            {I18n.t('js.ride.map.estimate_duration.menu')}
                        </div>
                        {#each Object.keys(RIDE_TYPE_ICON) as rideType (rideType)}
                            <button type="button"
                                    class="flex items-center gap-2 w-full px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
                                    class:bg-primary-100={$mapStore.currentRideType === rideType}
                                    onclick={() => _handleRideTypeSelect(rideType)}>
                                <Icon class="h-4 w-4"
                                      icon={RIDE_TYPE_ICON[rideType]}/>
                                {I18n.t(`js.ride.enums.ride_type.${rideType}`)}
                            </button>
                        {/each}
                    </div>
                {/if}
            {/if}
        </div>

        <div class="flex items-center gap-2.5 md:gap-[22px]">
            <div class="outline-data">
                <div class="outline-value"
                     class:outline-value-on={$mapStore.currentSpeed}>
                    {Computation.formatSpeed($mapStore.currentSpeed, {
                        unit: $userStore.unit,
                        unitTarget: 'mph'
                    }) || '-'}
                </div>

                <div class="outline-name">
                    {I18n.t('js.ride.map.summary.speed')}
                </div>
            </div>

            {#if PaceRideTypes.includes($mapStore.currentRideType)}
                <div class="outline-data">
                    <div class="outline-value"
                         class:outline-value-on={$mapStore.currentSpeed}>
                        {Computation.formatPace($mapStore.currentSpeed, {unit: $userStore.unit}) || '-'}
                    </div>

                    <div class="outline-name">
                        {I18n.t('js.ride.map.summary.pace')}
                    </div>
                </div>
            {/if}
        </div>
    </div>
</div>
